import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import moment from "moment";
import queryString from "query-string";
import ReactPaginate from "react-paginate";
import _ from "lodash";

import { STARTING_PAGE, DEBOUNCE_SEARCH } from "../../constants/constants";
import { renderResponseAsString } from "../../utils/Helpers";

import Spinner from "../../components/generics/Spinner";
import Filter from "../../components/generics/Filter";
import ListHeader from "../../components/generics/ListHeader";
import ToggleAlertLink from "../../components/generics/ToggleAlertLink";

import Alert from "../../utils/Alert";

import {
  fetchPricesListErrors,
  fetchPricesListErrorsSuccess,
  fetchPricesListErrorsFailure,
  reprocessPrice,
  reprocessPriceSuccess,
  reprocessPriceFailure,
  fetchPricesListDetail,
  fetchPricesListDetailSuccess,
  fetchPricesListDetailFailure,
} from "../../store/priceLists/actions";

import ModalReprocessPrice from "../../components/priceLists/ModalReprocessPrice/ModalReprocessPrice";
import PricesListErrors from "../../components/priceLists/PricesListErrors";
import { withRouter } from "utils/WithRouter";

const PriceListErrorsContainer = ({
  history,
  match,
  location,
  fetchPricesListErrors,
  priceListErrors,
  fetchPricesListDetail,
  priceListDetail,
  reprocessPrice,
  itemsList,
}) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(queryString.parse(location.search).search);
  const [header, setHeader] = useState("");
  const [selectedOffer, setSelectedOffer] = useState(undefined);
  const alertRef = useRef(null);
  const [isOpenReprocessPriceModal, setOpenReprocessPriceModal] = useState(false);

  useEffect(() => {
    const params = queryString.parse(location.search);
    const page = params.page ? params.page : STARTING_PAGE;
    const search = params.search ? params.search : "";
    fetchPricesListDetail(match.params.id);
    fetchPricesListErrors(match.params.id, search, page);
  }, []);

  useEffect(() => {
    if (!priceListDetail.loading && priceListDetail.priceList) {
      setHeader(`${priceListDetail.priceList.vendor_name} - ${moment(priceListDetail.priceList.date_created).utc().format("L")}`);
    }
  }, [priceListDetail]);

  useEffect(() => {
    debounceSearch();
    return debounceSearch.cancel;
  }, [page, search]);

  const toggleReprocessPriceModal = (offer = undefined) => {
    setSelectedOffer(offer);
    setOpenReprocessPriceModal(!isOpenReprocessPriceModal);
  };

  const handleReprocessPrice = (data) => {
    setOpenReprocessPriceModal(false);
    reprocessPrice(data, () => fetchPricesListErrors(match.params.id, search, page), alertRef);
  };

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------

  const onSearchChange = (event) => {
    const search = event.target.value ? event.target.value : "";
    setSearch(search);
    setPage(STARTING_PAGE);
  };

  const filterList = () => {
    let params = {};
    params.search = search;
    params.page = page;

    history.replace({
      search: queryString.stringify(params),
    });
    fetchPricesListErrors(match.params.id, search, page);
  };

  const handlePageRequest = (data) => {
    let newPage = data.selected + 1;
    if (newPage !== page) {
      setPage(newPage);
    }
  };

  const debounceSearch = _.debounce(filterList, DEBOUNCE_SEARCH);
  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="animated fadeIn">
      <Alert ref={alertRef} />
      <div className="card">
        <ListHeader dark showAction={false} title={header} iconClass="fa fa-file-text-o mr-05" />
        <Filter
          searchLabel2="Search"
          searchPlaceholder2="Search by description, brand or item code"
          searchVal2={search}
          onSearchChange2={onSearchChange}
          colClass="col-12 col-sm-3"
        />
      </div>
      {priceListDetail.loading ? (
        <Spinner />
      ) : (
        <div>
          <ToggleAlertLink
            title={"Please select a solution for the unprocessed prices"}
            actionLink={`/price_lists/${match.params.id}?page=1`}
            actionTitle={"Go back to list"}
          />
          {priceListErrors.loading ? (
            <Spinner />
          ) : (
            <div className="card">
              <div className="card-block p-0">
                <table className="table table-striped table-bordered datatable mb-0">
                  <thead>
                    <tr>
                      <th>Item code</th>
                      <th>Error description</th>
                      <th>Unprocessed data</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <PricesListErrors pricesList={priceListErrors.errors} toggleReprocessPriceModal={toggleReprocessPriceModal} />
                </table>
                {priceListErrors.pages > 1 && (
                  <ReactPaginate
                    pageCount={priceListErrors.pages}
                    pageRangeDisplayed={2}
                    marginPagesDisplayed={1}
                    previousLabel="<"
                    nextLabel=">"
                    onPageChange={handlePageRequest}
                    initialPage={page - 1}
                    disableInitialCallback={true}
                    containerClassName="pagination"
                    pageClassName="page-item"
                    pageLinkClassName="page-link"
                    activeClassName="page-item active"
                    previousClassName="paginate_button previous"
                    nextClassName="paginate_button next"
                    disabledClassName="page-item disabled"
                    forcePage={page - 1}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {isOpenReprocessPriceModal && (
        <ModalReprocessPrice
          isModalOpen={isOpenReprocessPriceModal}
          closeModal={toggleReprocessPriceModal}
          onSubmit={handleReprocessPrice}
          selectedOffer={selectedOffer}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    priceListDetail: state.priceLists.priceListDetail,
    priceListErrors: state.priceLists.priceListErrors,
    itemsList: state.inventories.itemsList,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchPricesListErrors: (id, search, page) => {
      dispatch(fetchPricesListErrors(id, search, page)).payload.then(
        (response) => {
          dispatch(fetchPricesListErrorsSuccess(response.data));
        },
        (error) => {
          dispatch(fetchPricesListErrorsFailure(error));
        }
      );
    },
    fetchPricesListDetail: (id) => {
      dispatch(fetchPricesListDetail(id)).payload.then(
        (response) => {
          dispatch(fetchPricesListDetailSuccess(response.data));
        },
        (error) => {
          dispatch(fetchPricesListDetailFailure(error));
        }
      );
    },
    reprocessPrice: (data, callback, alertRef) => {
      dispatch(reprocessPrice(data)).payload.then(
        (response) => {
          dispatch(reprocessPriceSuccess(response.data));
          callback();
          alertRef.current.sendAlert(`The price was successfully processed`, "success");
        },
        (error) => {
          dispatch(reprocessPriceFailure(error));
          callback();
          const response =
            error.response.status === 500
              ? error.response.statusText
              : `Another error rised while processing price: ${renderResponseAsString(error.response.data)}`;
          alertRef.current.sendAlert(response, "error");
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PriceListErrorsContainer));
